import { Nullable } from "common/types";
import { List, Map, Record } from "immutable";
import { RootActions, RootState } from "common/reducers";
import { UserRecord, userFromJSON } from "common/records/User";
import { reducer as orgReducer } from "common/reducers/org.reducer";
import { OrgActions } from "common/reducers/org.actions";
import { reducer as accessReducer } from "common/reducers/access.reducer";
import { AccessActions } from "common/reducers/access.actions";
import { AllowedAclRecord, allowedAclFromJSON } from "common/records/AllowedAclRecord";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import {
  ADD_ACCESSES,
  FETCH_CURRENT_USER,
  FETCH_USER,
  RECEIVE_ALLOWED_ACL,
  RECEIVE_USER,
  RECEIVE_USERS,
  REMOVE_CURRENT_USER,
  RESET_USER,
  SET_CURRENT_USER,
  SET_CURRENT_USER_ERRORS,
  SET_USER_LOADED,
  UPDATE,
  REMOVE,
  CONCAT,
  UserActions,
} from "common/reducers/user.actions";
import { currentUser } from "common/selectors/user.selectors";

export const UserStateRecord = Record({
  currentUser: null as Nullable<UserRecord>,
  byId: Map<number, UserRecord>(),
  lastSavedById: Map<number, UserRecord>(),
  // Map<string(userId:accessUid), Map<string(template), AllowedAclRecord>>
  allowedAclByUserAccess: Map<string, Map<string, AllowedAclRecord>>(),
});

export const initialState = UserStateRecord();

export type UserState = typeof initialState;

export const model = "users";
export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case FETCH_CURRENT_USER:
      return state.setIn([model, "currentUser"], userFromJSON({ email: action.payload.email, loading: true }));

    case SET_CURRENT_USER:
      if (action.payload.user.org) {
        state = orgReducer(state, OrgActions.receiveOrg(action.payload.user.org));
        state = orgReducer(state, OrgActions.receiveOrgOverviews([action.payload.user.org]));
      }
      return state.setIn([model, "currentUser"], userFromJSON(action.payload.user));

    case SET_CURRENT_USER_ERRORS: {
      const errors = action.payload.errors;
      if (state.getIn([model, "currentUser"])) {
        return state.setIn([model, "currentUser", "errors"], errors);
      }
      return state.setIn([model, "currentUser"], userFromJSON({ errors }));
    }

    case REMOVE_CURRENT_USER:
      return state.setIn([model, "currentUser"], null);

    case RECEIVE_USER:
      const { user } = action.payload;
      const immutableUser = userFromJSON(user);
      if (user.org) {
        state = orgReducer(state, OrgActions.receiveOrg(user.org));
      }
      if (user.accesses?.length > 0) {
        user.accesses.forEach((access) => {
          state = accessReducer(state, AccessActions.setAccess(access.id, access));
        });
      }
      return receive(state, model, immutableUser);

    case RESET_USER: {
      const user = state.getIn([model, "byId", action.payload.userId]) as UserRecord;
      user.accesses.forEach((access) => {
        state = accessReducer(state, AccessActions.resetAccess(access));
      });
      return reset(state, model, action.payload.userId);
    }
    case FETCH_USER:
      return fetch(state, model, userFromJSON({ id: action.payload }));

    case SET_USER_LOADED:
      return loaded(state, model, action.payload);

    case RECEIVE_USERS:
      const users = action.payload;
      users.forEach((u) => {
        state = reducer(state, UserActions.receiveUser(u));
      });
      return state;

    case RECEIVE_ALLOWED_ACL: {
      const { userId, accessUid, template, allowedAcl } = action.payload;
      const userIdAccessUid = `${userId}:${accessUid}`;
      const allowedAclRecord = allowedAclFromJSON(allowedAcl);
      return state.setIn([model, "allowedAclByUserAccess", userIdAccessUid, template || "none"], allowedAclRecord);
    }

    case ADD_ACCESSES: {
      const { userId, accessIds } = action.payload;
      if (!state.getIn([model, "byId", userId])) {
        const userRecord = userFromJSON({ id: userId }).set("accesses", List(accessIds));
        return state.setIn([model, "byId", userId], userRecord).setIn([model, "lastSavedById", userId], userRecord);
      }
      return state
        .updateIn([model, "byId", userId, "accesses"], List(), (accesses: List<number>) => {
          return List(new Set(accesses.toArray().concat(accessIds)));
        })
        .updateIn([model, "lastSavedById", userId, "accesses"], List(), (accesses: List<number>) => {
          return List(new Set(accesses.toArray().concat(accessIds)));
        });
    }
    case UPDATE: {
      const { path, name, value } = action.payload;
      const rootPath = [model, "byId", currentUser(state)?.get("id")].concat(path);
      return state.setIn(rootPath.concat(name.split(".")), value);
    }

    case REMOVE: {
      const { path, name, value } = action.payload;
      const rootPath = [model, "byId", currentUser(state)?.get("id")].concat(path);
      return state.updateIn(rootPath.concat(name.split(".")), (values: List<any>) => {
        const index = values.indexOf(value);
        if (index < 0) return values;
        return values.delete(index);
      });
    }

    case CONCAT: {
      const { path, name, value } = action.payload;
      const rootPath = [model, "byId", currentUser(state)?.get("id")].concat(path);
      return state.updateIn(rootPath.concat(name.split(".")), (list: List<any>) => (list || List()).concat(value));
    }

    default:
      return state;
  }
};
